import { useContext, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from 'redux/hooks';

import { BucketContext, type S3Item } from 'contexts/BucketContext';

import { InfoItem } from 'pages/Info/sections/Others/components/InfoItem';

import * as S from './Others.styles';
import { ListInfoItem } from './example.mock';

const useOthers = () => {
  const { getListItems, getUrl } = useContext(BucketContext);
  const { season, marketLine } = useAppSelector((state) => state.settings);
  const [isActive, setIsActive] = useState<string>('');
  const [data, setData] = useState<ListInfoItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const PREFIX = useMemo(
    () =>
      season && marketLine
        ? `catalogue/products/${season}/${marketLine.toLocaleLowerCase()}/info/`
        : '',
    [season, marketLine]
  );

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        if (!PREFIX) {
          setData([]);
          return;
        }
        setIsLoading(true);
        const res = await getListItems(PREFIX);
        const contents: { Key: string }[] = res?.Contents || [];

        const names = new Set<string>();
        contents.forEach(({ Key }) => {
          if (!Key || !Key.startsWith(PREFIX)) return;
          const rest = Key.slice(PREFIX.length);
          const first = rest.split('/')[0];
          if (first) names.add(first);
        });

        const infoNames = Array.from(names).sort((a, b) => a.localeCompare(b));

        const built: ListInfoItem[] = await Promise.all(
          infoNames.map(async (name, idx) => {
            const thumbnailKey = contents.find(({ Key }) => {
              if (
                !Key ||
                !Key.startsWith(`${PREFIX}${name}/thumbnail/`) ||
                Key.endsWith('/')
              ) {
                return false;
              }
              const extension = Key.toLowerCase().split('.').pop();
              return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '');
            })?.Key;

            const fileKey = contents.find(
              ({ Key }) =>
                Key &&
                Key.startsWith(`${PREFIX}${name}/file/`) &&
                !Key.endsWith('/') &&
                Key.toLowerCase().endsWith('.pdf')
            )?.Key;

            const [imageUrl, fileUrl] = await Promise.all([
              thumbnailKey
                ? getUrl(thumbnailKey)
                    .then((r: S3Item) => r.url)
                    .catch(() => undefined)
                : undefined,
              fileKey
                ? getUrl(fileKey)
                    .then((r: S3Item) => r.url)
                    .catch(() => undefined)
                : undefined,
            ]);

            return {
              id: name,
              label: name,
              image: imageUrl,
              file: fileUrl,
            } as ListInfoItem;
          })
        );

        if (isMounted) {
          setData(built);
          if (built.length > 0 && !isActive) {
            setIsActive(built[0].id);
          }
        }
      } catch (e) {
        if (isMounted) {
          setData([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [getListItems, getUrl, PREFIX]);

  const listInfoMapped = useMemo(
    () =>
      data?.map((item: ListInfoItem) => (
        <S.Li
          key={item.id}
          onClick={() => setIsActive(item.id)}
          isActive={isActive === item.id}
        >
          {item.label}
        </S.Li>
      )),
    [data, isActive]
  );

  const infoItemsMapped = useMemo(
    () =>
      data?.map((item: ListInfoItem) =>
        item.id === isActive ? (
          <InfoItem key={item.id} title={item.label} image={item.image} file={item.file} />
        ) : null
      ),
    [data, isActive]
  );

  return { listInfoMapped, infoItemsMapped, isLoading };
};
export { useOthers };
